import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useSnackbars } from '@customerio/pluma-components/react';
import { z } from '@customerio/hydra-validation';
import type { ObjectAttributesEditorState } from '@/features/objects';
import { getApiClientErrorBody, getApiClientErrorStatus, getUserFacingErrorMessage } from '@/services/api/errors';
import { OBJECT_ID_KEY } from '@/services/db/object/entity';
import { queueObjectCreate } from '@/services/db/object/mutations';
import { getObjects } from '@/services/db/object/queries';
import { objectTypeKeys, updateObjectTypeSentAttributes, type ObjectType } from '@/services/db/object-type/entity';
import { generatePath } from '@/services/router/generate-path';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { queryEncode } from '@/utils/condition-query-encoding';

/** The object create is queued; check once after this delay whether it landed. */
const CREATE_POLL_DELAY_MS = 3000;
const CREATE_SNACKBAR_ID = 'object.create';

const FieldErrorEnvelope = z.object({
  errors: z
    .array(z.looseObject({ detail: z.string().optional(), source: z.object({ pointer: z.string() }).optional() }))
    .nonempty(),
});

function firstFieldError(error: unknown): { field: string; detail: string } | null {
  const parsed = FieldErrorEnvelope.safeParse(getApiClientErrorBody(error));
  if (!parsed.success) return null;
  const [{ detail, source }] = parsed.data.errors;
  if (detail == null || source == null) return null;
  return { field: source.pointer.replace('/data/attributes/', ''), detail };
}

type UseCreateObjectFlowOptions = {
  objectType: ObjectType;
  workspaceId: number;
  editor: ObjectAttributesEditorState;
};

/**
 * The create-object save flow: queue the create, then check once after a short
 * delay whether the object landed — navigating to its detail page when it did,
 * or back to the type list with a "will be added soon" note when it did not.
 */
export function useCreateObjectFlow({ objectType, workspaceId, editor }: UseCreateObjectFlowOptions) {
  const router = useRouter();
  const safeNavigate = useSafeNavigate();
  const queryClient = useQueryClient();
  const snackbars = useSnackbars();
  const [isSaving, setIsSaving] = useState(false);
  // The leave guard's callback outlives a render, so it reads dirtiness through
  // a ref — the post-save reset() has to be visible to a navigation fired in
  // the same tick.
  const isDirtyRef = useRef(false);
  isDirtyRef.current = editor.isDirty;

  const listHref = router.buildLocation({
    to: '/workspaces/$workspaceId/objects/$slug',
    params: { workspaceId, slug: objectType.slug },
  }).href;

  const resetAndNavigate = (href: string) => {
    editor.reset();
    isDirtyRef.current = false;
    safeNavigate(href);
  };

  const reportCreateError = (error: unknown) => {
    const status = getApiClientErrorStatus(error);
    const fieldError = firstFieldError(error);
    // A field error is only "handled" when a row actually carries it; a 422
    // without a usable pointer still needs visible feedback.
    if (fieldError != null && editor.rows.some((row) => row.key === fieldError.field)) {
      editor.setServerError(fieldError.field, fieldError.detail);
      return;
    }
    const message =
      status === 403
        ? 'You do not have permission to create this object. Contact your Admin.'
        : getUserFacingErrorMessage(error, 'Something went wrong. Try again.');
    snackbars.create({ variant: 'critical', id: 'object.error', message });
  };

  const findCreatedObject = async (objectId: string) => {
    const filters = queryEncode({
      and: [
        { object_attribute: { field: OBJECT_ID_KEY, value: objectId, operator: 'eq', type_id: Number(objectType.id) } },
      ],
    });
    const { objects } = await getObjects(workspaceId, { objectTypeId: objectType.id, filters }, queryClient);
    return objects[0] ?? null;
  };

  const waitForCreatedObjectAndNavigate = async (objectId: string) => {
    snackbars.create({
      variant: 'default',
      id: CREATE_SNACKBAR_ID,
      message: 'Creating your object…',
      // zero disables the auto-close
      autoCloseTimeout: 0,
    });
    await new Promise((resolve) => setTimeout(resolve, CREATE_POLL_DELAY_MS));

    try {
      const record = await findCreatedObject(objectId);
      if (record == null) throw new Error('not created yet');
      snackbars.destroy(CREATE_SNACKBAR_ID);
      // The object detail page is still served by the embedded Ember app.
      resetAndNavigate(
        generatePath('/workspaces/:workspaceId/journeys/objects/:objectSlug/:objectId', {
          workspaceId,
          objectSlug: objectType.slug,
          objectId: record.id,
        }),
      );
    } catch {
      snackbars.create({ variant: 'default', id: CREATE_SNACKBAR_ID, message: 'Your object will be added soon.' });
      resetAndNavigate(listHref);
    }
  };

  /** Resolves false when the create was rejected — the error is already reported on the page. */
  const save = async (): Promise<boolean> => {
    const { attrs, hiddenAttrs } = editor.getModelAttributes();
    setIsSaving(true);
    try {
      let savedAttributes;
      try {
        savedAttributes = await queueObjectCreate({
          workspaceId,
          objectTypeId: objectType.id,
          objectId: attrs[OBJECT_ID_KEY] ?? '',
          attributes: attrs,
          hiddenAttributes: hiddenAttrs,
        });
      } catch (error) {
        reportCreateError(error);
        return false;
      }

      queryClient.setQueryData<ObjectType[]>(objectTypeKeys.list(workspaceId), (types) =>
        types?.map((type) =>
          type.id === objectType.id ? updateObjectTypeSentAttributes(type, savedAttributes) : type,
        ),
      );
      await waitForCreatedObjectAndNavigate(attrs[OBJECT_ID_KEY] ?? '');
      return true;
    } finally {
      setIsSaving(false);
    }
  };

  const discard = () => {
    resetAndNavigate(listHref);
  };

  return { isSaving, save, discard, hasUnsavedChanges: () => isDirtyRef.current };
}
